import type { PlanningCalcResult } from './planningSummaryCalculations';
import { formatMeasure } from './formatMeasure';

/**
 * Number / unit formatting helpers shared by the Planning Summary card.
 *
 * Track: spray_session_ui_changes_20260502 / Phase 2 / Task 3.
 *
 * Formatting rules from spec §2:
 *   - Litres-of-water figures: thousand-separators, no decimals (e.g. `2,400 L`).
 *   - Per-product amounts: delegated to the shared `formatMeasure` helper —
 *     capped at 2 decimals and normalised to the best unit in the value's
 *     family (e.g. `4800 g` → `4.8 kg`, `0.55 L` → `550 ml`).
 *   - Tank count: pluralised label via i18n (`1 Tank` / `3 Tanks`).
 */

/** Litres-of-water figure: thousand-separators, no decimals. */
export function formatLitres(value: number): string {
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 0 })} L`;
}

/**
 * Per-product amount: delegates to the shared `formatMeasure` display
 * formatter (2dp cap, normalised to the best unit in the value's family).
 */
export function formatProductAmount(result: PlanningCalcResult): string {
  return formatMeasure(result.value, result.unit);
}

/**
 * Tank count with pluralised label. The translation function is injected
 * so this helper stays decoupled from react-i18next and easy to unit
 * test (the component passes its `t` from `useTranslation()`).
 */
export function formatTankCount(value: number, t: (key: string) => string): string {
  const key = value === 1 ? 'spray.planningSummary.tank' : 'spray.planningSummary.tanks';
  return `${value} ${t(key)}`;
}
